<template>
  <UikitFormField
    ref="$field"
    :model-value="modelValue"
    :name="name"
    :label="label"
    :rules="rules"
    :required="required"
    :no-error-message="noErrorMessage"
    :error-text="errorText"
    :disabled="disabled"
    class="osk-number-picker"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <div
      :class="{
        'osk-number-picker__content': true,
        'osk-number-picker__content_disabled': disabled,
        'osk-number-picker__content_small': small,
        'osk-number-picker__content_loading': loading,
      }"
    >
      <UikitButton
        type="button"
        icon="Minus"
        icon-size="s"
        color="none"
        class="osk-number-picker__button"
        :disabled="decrementDisabled"
        @click="decrement"
      />

      <Transition name="fade">
        <UikitLoaderInner
          v-if="loading"
          class="osk-number-picker__loader"
          small
        />

        <span v-else>
          {{ currentValue }}

          <template v-if="small && postfix">
            {{ props.postfix }}
          </template>
        </span>
      </Transition>

      <UikitButton
        type="button"
        icon="Plus"
        icon-size="s"
        color="none"
        class="osk-number-picker__button"
        :disabled="incrementDisabled"
        @click="increment"
      />
    </div>
  </UikitFormField>
</template>

<script setup lang="ts">
import type { FieldContext } from '~/types/components/form';

const props = withDefaults(defineProps<{
  modelValue?: number
  name: string
  label?: string
  disabled?: boolean
  rules?: string
  required?: boolean
  noErrorMessage?: boolean,
  errorText?: string,
  postfix?: string,
  loading?: boolean
  min?: number
  max?: number
  small?: boolean
}>(), {
  modelValue: undefined,
  label: undefined,
  rules: undefined,
  errorText: undefined,
  min: -Infinity,
  max: -Infinity,

});

const emits = defineEmits<{
  (e: 'update:modelValue', value: any): void,
}>();

const $field = ref<FieldContext>();

const currentValue = computed(() => $field?.value?.value as number);

const decrementDisabled = computed(() => currentValue.value <= props.min);
const incrementDisabled = computed(() => currentValue.value >= props.max);

function decrement() {
  updateValue(-1);
}

function increment() {
  updateValue(1);
}

function updateValue(num: number) {
  if (!$field.value || props.disabled) return;

  const { setValue } = $field.value;

  let result = currentValue.value + num;

  if (result < props.min) {
    result = props.min;
  }

  if (result > props.max) {
    result = props.max;
  }

  setValue(result);
}
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-number-picker {
  &__content {
    height: 40px;
    line-height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &_small {
      font-weight: 600;
      font-size: 12px;
      height: 25px;
      line-height: 25px;
      min-width: 71px;

      .osk-number-picker__button {
        width: 25px;
        height: 25px;
      }
    }

    &_loading {
      pointer-events: none;
    }
  }

  &__button {
    &:first-child {
      margin-left: -12px;
    }

    &:last-child {
      margin-right: -12px;
    }
  }

  :deep(.osk-field__inner_disabled) {
    background-color: $white;
    color: $black;
  }
}
</style>
